import type { CollectionKind, ContentType } from '@prisma/client';
import { z } from 'zod';
import type { ContentNodeClass } from '@/logic/content/graph-model.js';

export type DeletionEdgeKind =
  | 'ATTACHMENT'            // ContentAttachment child, optional
  | 'COLLECTION_MEMBER'     // member of a COLLECTION being deleted, forced
  | 'DOCUMENT_HIERARCHY'    // Document.path descendant, forced
  | 'MESSAGE_THREAD';       // Message.rootCode descendant of a thread root, forced

export type DeletionInclusion = 'ROOT' | 'FORCED' | 'OPTIONAL';

export type DeletionBlockReason =
  | 'PROVENANCE_APEX_HAS_CHILDREN'
  | 'PROVENANCE_APEX_HAS_EXECUTIONS'
  | 'LIVING_DOCUMENT_TARGET'
  | 'TEMPLATE_DEFINITION'
  | 'NO_DELETE_PERMISSION';

export type DeletionWarningKind =
  | 'TRANSMITTAL_EVIDENCE'
  | 'PROVENANCE_DETACH'
  | 'MEDIA_BLOB'
  | 'REVISION_ROWS';

export interface DeletionWarning {
  kind: DeletionWarningKind;
  count: number;
  detail: string;          // one member-facing sentence, no ids
}

export interface SweptContainer {
  contentId: number;
  code: string;
}

export interface DeletionImpactNode {
  contentId: number;
  code: string;
  contentType: ContentType;
  collectionKind: CollectionKind | null;
  title: string | null;
  nodeClass: ContentNodeClass;
  inclusion: DeletionInclusion;
  treeParentId: number | null;     // position in the presented tree only
  edgeKind: DeletionEdgeKind | null;
  depth: number;
  alsoReachableFrom: number[];     // other in-set parents, first path wins
  revisionRowCount: number;        // rows sharing this code, head included
  orphanCount: number;             // live attachment children NOT in the set
  blocked: DeletionBlockReason[];
  warnings: DeletionWarning[];
}

export interface DeletionImpactSummary {
  selectedCount: number;           // ROOT nodes not blocked
  forcedCount: number;
  optionalCount: number;
  blockedCount: number;
  // What this delete actually removes. NOT selectedCount + forcedCount: an OPTIONAL
  // node stays OPTIONAL after the caller opts it in, so those two never account for
  // it. This is the number to show a member and confirm against.
  goingCount: number;
  goingOptionalCount: number;      // of goingCount, the ones the caller opted in
  revisionRowCount: number;        // total rows the delete would remove
  orphanCount: number;             // distinct rows left with ZERO surviving parent edges
  containerReapCandidateIds: number[]; // apexes and collections outside the set left empty by this delete
  transmittalCount: number;
  mediaBlobCount: number;
}

export interface DeletionImpact {
  roots: number[];
  nodes: DeletionImpactNode[];
  summary: DeletionImpactSummary;
  truncated: boolean;
  truncatedCount: number;          // nodes omitted by the cap
  planHash: string;
}

export const deletionPreviewInputSchema = z.object({
  roots: z.array(z.number().int().positive()).min(1).max(200),
  optionalIncluded: z.array(z.number().int().positive()).max(2000).optional(),
}).strict();

export type DeletionPreviewInput = z.infer<typeof deletionPreviewInputSchema>;

export const deletionCommitInputSchema = z.object({
  roots: z.array(z.number().int().positive()).min(1).max(200),
  optionalIncluded: z.array(z.number().int().positive()).max(2000).default([]),
  planHash: z.string().min(16),
}).strict();

export type DeletionCommitInput = z.infer<typeof deletionCommitInputSchema>;

export interface DeletionCommitResult {
  deletedContentIds: number[];      // every Content row removed, revisions included
  deletedNodeCount: number;         // logical nodes, revisions collapsed
  skipped: Array<{ contentId: number; reason: DeletionBlockReason }>;
  failed: Array<{ contentId: number; errorCode: string; errorMessage: string }>;
  orphanedContentIds: number[];     // rows left with zero parent edges
  reapedContainerIds: number[];     // apexes and collections auto-discarded by the empty-container sweep
  nowEmptyApexIds: number[];        // apexes left childless, NOT deleted
  alreadyGone: number[];            // plan nodes already absent when the batch reached them
}
